import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageSquare, Scale, FileText, HeartPulse, GraduationCap, Rocket, Zap, Clock, MapPin, Ban } from "lucide-react";
import { BrandImage } from "@/components/Brand";
import Seo from "@/components/Seo";
import { LineReveal } from "@/components/LineReveal";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { LeadForm } from "@/components/LeadForm";
import { SERVICES } from "@/data/services";
import api from "@/lib/api";

const ICONS = { MessageSquare, Scale, FileText, HeartPulse, GraduationCap, Rocket };

const CHAPTERS = [
  { n: "01", title: "On your side", text: "We work for you, not for the process. When things get messy, our only agenda is your side of the table." },
  { n: "02", title: "Human, not corporate", text: "You talk to the person doing the work. Plain English, no scripts, no account managers reading from flowcharts." },
  { n: "03", title: "Fast & decisive", text: "Same-day answers and fixed-fee quotes. When a people problem is live, momentum beats perfection." },
  { n: "04", title: "Legal teeth, when needed", text: "Led by a practising solicitor. When a matter needs legal advice, representation or privilege, we can introduce you to Bonsai Law, an SRA-regulated firm, or hand everything over to any solicitor you choose. The choice is always yours." },
];

const CASES = [
  { tag: "Grievance", title: "A 14-person agency facing its first grievance", result: "Process planned within 48 hours, resolved at the first formal meeting." },
  { tag: "Startup HR", title: "A Kent startup hiring fast, contracts a mess", result: "Full foundations, including contracts, handbook and probation, delivered in two weeks." },
  { tag: "Disciplinary", title: "A founder avoiding a hard conversation for months", result: "Scripted, supported and closed in nine days. Team noticeably lighter." },
];

export default function Home() {
  const [posts, setPosts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    api.get("/blog/posts").then((r) => setPosts((r.data.posts || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title="Fixed-Fee HR Support for UK SMEs, Same-Day Answers | Electra HR"
        siteName="Electra HR"
        description="Fixed-fee HR consultancy for UK founders and SMEs of 5 to 50 people. Same-day answers, no retainers, backed by SRA-regulated Bonsai Law when a matter turns legal."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "@id": `${origin}/#professionalservice`,
          name: "Electra HR",
          image: `${origin}/images/electra-hr-logo.png`,
          url: origin,
          telephone: "+44 1622 957300",
          priceRange: "£££",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Suite 100, 7 Kenden Business Park, Maritime Close",
            addressLocality: "Rochester",
            addressRegion: "Kent",
            postalCode: "ME2 4JF",
            addressCountry: "GB",
          },
          areaServed: { "@type": "Country", name: "United Kingdom" },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "HR Services",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Day-to-Day HR Advice", url: `${origin}/services/day-to-day-hr-advice` } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Grievances & Disciplinaries", url: `${origin}/services/grievances-disciplinaries` } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Contracts & Handbooks", url: `${origin}/services/contracts-handbooks` } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "HR Health Checks", url: `${origin}/services/hr-health-checks` } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Training & Change", url: `${origin}/services/training-and-change` } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Startup HR Packages", url: `${origin}/services/startup-packages` } },
            ],
          },
        }}
      />

      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="grain relative overflow-hidden bg-ink text-paper">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 pt-32 pb-20 sm:pt-44 sm:pb-28">
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              className="eyebrow text-pink"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6 }}
            >
              Electra HR · UK-wide HR consultancy · Kent roots
            </motion.p>
            <LineReveal
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight"
              lines={[
                "HR on your side.",
                <span key="l2" className="text-pink">Not the paperwork's.</span>,
              ]}
            />
            <motion.p
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-paper/70"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              Straight-talking HR for founders and SMEs of 5 to 50 people. Same-day answers, no
              retainers, and qualified HR support backed by Bonsai Law, an SRA-regulated firm,
              when a matter turns legal.
            </motion.p>
            <motion.div
              className="mt-9 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to="/contact"
                data-testid="hero-cta-book-call"
                className="group flex items-center gap-2 bg-pink px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-1"
              >
                Get a same-day answer
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="flex items-center gap-2 border-2 border-white/25 px-7 py-4 font-bold text-paper transition-colors hover:border-pink hover:text-pink"
              >
                See what we handle
              </Link>
            </motion.div>
            <motion.div
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-[0.18em] text-paper/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <span className="flex items-center gap-2"><Clock size={14} className="text-pink" /> Same-day answers</span>
              <span className="flex items-center gap-2"><Ban size={14} className="text-pink" /> Zero retainers</span>
              <span className="flex items-center gap-2"><MapPin size={14} className="text-pink" /> Rochester, Kent → UK-wide</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="clip-frame relative overflow-hidden border border-white/10">
                <motion.div style={{ y: imgY }} className="h-[480px] w-full scale-110">
                  <BrandImage
                    src="/images/hero-laptop-900.webp"
                    srcSet="/images/hero-laptop-480.webp 480w, /images/hero-laptop-900.webp 900w, /images/hero-laptop-1400.webp 1400w"
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    fallback="/images/hero-laptop.jpg"
                    alt="Vanessa Challess, founder of Electra HR, typing at her desk"
                    loading="eager"
                    fetchPriority="high"
                    className="h-full w-full object-cover"
                  />
                </motion.div>
                <div className="absolute inset-0 bg-ink/25" />
              </div>
              <div className="absolute -bottom-6 -left-6 border border-white/10 bg-ink-soft p-5 shadow-2xl">
                <p className="eyebrow text-pink">Backed by Bonsai Law</p>
                <p className="mt-1.5 max-w-[220px] text-sm text-paper/70">
                  An introduction to an SRA-regulated firm when a matter turns legal, if you want one.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={["Plain English", "No retainers", "Same-day answers", "On your side", "UK-wide", "Kent roots", "Female-founded"]} />

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="bg-paper py-20 sm:py-28">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-ink/50">The manifesto</p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Four things we believe. Everything else follows.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08} className="bg-paper">
                <div data-testid={`manifesto-chapter-${c.n}`} className="group h-full p-8 sm:p-10 transition-colors duration-500 hover:bg-ink">
                  <p className="font-mono text-sm text-pink">{c.n}</p>
                  <h3 className="mt-4 font-display text-xl sm:text-2xl font-semibold tracking-tight transition-colors duration-500 group-hover:text-paper">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-ink/60 transition-colors duration-500 group-hover:text-paper/70">
                    {c.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section data-testid="services-section" className="bg-ink py-20 sm:py-28 text-paper">
        <div className="container-x">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow text-pink">What we do</p>
              <h2 className="mt-4 max-w-xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                Six services. Zero theatre.
              </h2>
            </div>
            <Link to="/services" data-testid="services-index-link" className="group flex items-center gap-2 font-bold text-pink">
              All services <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group flex h-full flex-col border border-white/10 bg-ink-soft p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-pink/60"
                  >
                    <Icon size={26} className="text-pink" strokeWidth={1.8} />
                    <h3 className="mt-6 font-display text-xl font-semibold tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-paper/55">{s.tagline}</p>
                    <span className="mt-auto pt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-paper/40 transition-colors group-hover:text-pink">
                      Dig in <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BONSAI BRIDGE */}
      <section data-testid="bonsai-bridge-section" className="grain relative bg-ink-soft py-20 sm:py-28 text-paper border-t border-white/10">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-pink">The bridge</p>
            <h2 className="mt-4 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              HR muscle now. Legal firepower if it's needed.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-stretch gap-6">
            <Reveal className="border border-white/10 bg-ink p-8 sm:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/40">You start here</p>
              <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold">Electra HR <span className="text-pink">your ally</span></h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-paper/60">
                An HR consultancy led by a practising solicitor. Advice, documents, processes and training,
                the day-to-day work that keeps your people problems small. Fixed fees, plain English, your corner.
              </p>
            </Reveal>
            <div className="flex items-center justify-center" aria-hidden="true">
              <div className="flex items-center gap-2 border border-pink/40 bg-ink px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-pink lg:flex-col lg:py-6">
                <span>When it turns legal</span>
                <ArrowRight size={16} className="lg:rotate-90" />
              </div>
            </div>
            <Reveal className="border border-white/10 bg-ink p-8 sm:p-10" delay={0.12}>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/40">If you choose</p>
              <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold">Bonsai Law <span className="text-pink">the legal teeth</span></h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-paper/60">
                A separate, SRA-regulated law firm. Legal advice, representation and the protection of legal
                professional privilege, with full context handed over, so you never explain your situation twice.
                You are free to instruct any solicitor instead, and we will pass your file to them with your consent.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-3xl text-xs leading-relaxed text-paper/40">
              Electra HR is an HR consultancy, not a law firm, and is not SRA-regulated. Legal services are
              provided separately by Bonsai Law under SRA regulation, only when your matter genuinely needs them and you
              choose to instruct it. Which solicitor you use is always your choice.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER SPOTLIGHT */}
      <section data-testid="founder-section" className="bg-paper py-20 sm:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="clip-frame relative overflow-hidden">
              <BrandImage
                src="/images/vanessa-challess-480.webp"
                srcSet="/images/vanessa-challess-480.webp 480w, /images/vanessa-challess-900.webp 900w"
                sizes="(min-width: 1024px) 40vw, 100vw"
                fallback="/images/vanessa-challess.jpg"
                alt="Vanessa Challess, founder of Electra HR"
                className="h-[420px] w-full object-cover"
              />
            </div>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.18em] text-ink/45">
              Vanessa Challess, solicitor &amp; founder
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow text-ink/50">Who's in your corner</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                Led by a solicitor. Run like a startup's best friend.
              </h2>
              <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-ink/70">
                Electra HR was founded by Vanessa Challess, a practising litigation solicitor who watched too
                many founders get buried by
                corporate HR firms, retainer-locked, slow, and weirdly keen on process for its own sake.
                So Electra does the opposite: straight answers, honest pricing, and the confidence of knowing
                that if a situation turns legal, SRA-regulated lawyers at Bonsai Law are one introduction away, or we
                hand over to the solicitor of your choice.
              </p>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink/70">
                Based in Rochester, Kent. Working with founders across the UK.
              </p>
              <Link to="/about" data-testid="founder-story-link" className="group mt-8 inline-flex items-center gap-2 font-bold text-ink">
                Read the story <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CASE HIGHLIGHTS */}
      <section data-testid="cases-section" className="bg-paper pb-20 sm:pb-28">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-ink/50">Field notes</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              What "on your side" looks like in practice
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CASES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <article data-testid={`case-card-${i}`} className="h-full border-2 border-ink p-7 transition-transform duration-300 hover:-translate-y-1.5">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/45">{c.tag}</p>
                  <h3 className="mt-3 font-display text-lg font-semibold leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{c.result}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-mono text-xs text-ink/40">
            Illustrative examples based on typical engagements. Named client stories and reviews are on the way.
          </p>
        </div>
      </section>

      {/* BLOG TEASER */}
      <section data-testid="blog-teaser-section" className="bg-ink py-20 sm:py-28 text-paper">
        <div className="container-x">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow text-pink">Briefings</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                HR thinking, minus the waffle
              </h2>
            </div>
            <Link to="/blog" data-testid="blog-index-link" className="group flex items-center gap-2 font-bold text-pink">
              All briefings <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.length === 0 && [0, 1, 2].map((i) => (
              <div key={i} className="h-56 animate-pulse border border-white/10 bg-ink-soft" />
            ))}
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-teaser-${p.slug}`}
                  className="group flex h-full flex-col border border-white/10 bg-ink-soft p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-pink/60"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-pink">{p.category}</p>
                  <h3 className="mt-3 font-display text-lg font-semibold leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-paper/55 line-clamp-3">{p.excerpt}</p>
                  <span className="mt-auto pt-5 font-mono text-xs text-paper/35">{p.read_time} min read</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section data-testid="contact-section" className="grain relative bg-pink py-20 sm:py-28 text-ink">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="eyebrow text-ink/60">No intake forms from hell</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              Tell us what's going on. We'll take it from there.
            </h2>
            <p className="mt-6 max-w-md text-base sm:text-lg leading-relaxed text-ink/70">
              Three fields, one human on the other end, and an answer within one working day, usually much faster.
            </p>
            <div className="mt-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-ink/55">
              <Zap size={15} /> Rapid response, fixed-fee quotes
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-2 border-ink bg-paper p-7 sm:p-9 shadow-[10px_10px_0_0_#0A0E17]">
              <LeadForm source="home" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
